import React, { useState, useEffect, useRef } from 'react';

/**
 * MultiSelectChips: اختيار متعدد عبر buttons (للقوائم القصيرة 2-6 خيارات)
 * مثال: التدخين، الحالة الاجتماعية، نوع الزواج
 */
export const MultiSelectChips = ({ options, value = [], onChange, maxSelections = null, testidPrefix = 'chip' }) => {
  // ensure value is always array
  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  const toggle = (opt) => {
    const exists = selected.includes(opt);
    if (exists) {
      onChange(selected.filter(s => s !== opt));
    } else {
      if (maxSelections && selected.length >= maxSelections) {
        // استبدل الأقدم
        const updated = [...selected.slice(1), opt];
        onChange(updated);
      } else {
        onChange([...selected, opt]);
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {options.map(opt => {
        const val = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const isSelected = selected.includes(val);
        return (
          <button
            key={val}
            type="button"
            data-testid={`${testidPrefix}-${val}`}
            onClick={() => toggle(val)}
            style={{
              padding: '9px 16px',
              borderRadius: '20px',
              border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border-color)',
              background: isSelected ? 'rgba(255, 107, 157, 0.12)' : 'var(--card-bg)',
              color: isSelected ? 'var(--primary)' : 'var(--text-dark)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              whiteSpace: 'nowrap'
            }}
          >
            {isSelected && <span style={{ fontSize: '11px' }}>✓</span>}
            {label}
          </button>
        );
      })}
    </div>
  );
};

/**
 * MultiSelectDropdown: اختيار متعدد عبر dropdown (للقوائم الطويلة 7+ خيارات)
 * مثال: الجنسية، الدول، المجال الوظيفي
 */
export const MultiSelectDropdown = ({ options, value = [], onChange, placeholder = 'اختر...', testid = 'multi-dropdown' }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (opt) => {
    const val = typeof opt === 'string' ? opt : opt.value;
    if (selected.includes(val)) {
      onChange(selected.filter(s => s !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const removeItem = (val, e) => {
    e.stopPropagation();
    onChange(selected.filter(s => s !== val));
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }} data-testid={testid}>
      <div
        onClick={() => setOpen(!open)}
        className="input"
        style={{
          minHeight: '44px',
          cursor: 'pointer',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          alignItems: 'center',
          paddingTop: '8px',
          paddingBottom: '8px'
        }}
      >
        {selected.length === 0 && (
          <span style={{ color: 'var(--text-light)', fontSize: '14px' }}>{placeholder}</span>
        )}
        {selected.map(val => {
          const opt = options.find(o => (typeof o === 'string' ? o : o.value) === val);
          const label = opt ? (typeof opt === 'string' ? opt : opt.label) : val;
          return (
            <span
              key={val}
              style={{
                background: 'rgba(255, 107, 157, 0.12)',
                color: 'var(--primary)',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              {label}
              <span
                onClick={(e) => removeItem(val, e)}
                style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', opacity: 0.7 }}
              >
                ×
              </span>
            </span>
          );
        })}
        <span style={{ marginRight: 'auto', fontSize: '14px', color: 'var(--text-light)' }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            left: 0,
            marginTop: '4px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            maxHeight: '240px',
            overflowY: 'auto',
            zIndex: 100,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
          }}
        >
          {options.map(opt => {
            const val = typeof opt === 'string' ? opt : opt.value;
            const label = typeof opt === 'string' ? opt : opt.label;
            const isSelected = selected.includes(val);
            return (
              <div
                key={val}
                onClick={() => toggle(opt)}
                style={{
                  padding: '10px 14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: isSelected ? 'rgba(255, 107, 157, 0.08)' : 'transparent',
                  color: 'var(--text-dark)',
                  fontSize: '14px',
                  borderBottom: '1px solid var(--border-color)',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--light-bg)'}
                onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? 'rgba(255, 107, 157, 0.08)' : 'transparent'}
              >
                <span
                  style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid var(--primary)',
                    borderRadius: '4px',
                    background: isSelected ? 'var(--primary)' : 'transparent',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    flexShrink: 0
                  }}
                >
                  {isSelected && '✓'}
                </span>
                {label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * MultiTagInput: حقل إدخال متعدد (للنص الحر مثل الوظائف)
 * المستخدم يكتب → Enter → يصبح tag
 */
export const MultiTagInput = ({ value = [], onChange, placeholder = 'اكتب واضغط Enter...', testid = 'tag-input' }) => {
  const [input, setInput] = useState('');
  const tags = Array.isArray(value) ? value : (value ? [value] : []);

  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput('');
  };

  const removeTag = (tag) => {
    onChange(tags.filter(t => t !== tag));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div data-testid={testid}>
      <div
        className="input"
        style={{
          minHeight: '44px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          alignItems: 'center',
          paddingTop: '8px',
          paddingBottom: '8px'
        }}
      >
        {tags.map((tag, idx) => (
          <span
            key={idx}
            style={{
              background: 'linear-gradient(135deg, var(--primary), var(--purple))',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            {tag}
            <span
              onClick={() => removeTag(tag)}
              style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
            >
              ×
            </span>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addTag}
          placeholder={tags.length === 0 ? placeholder : ''}
          style={{
            flex: 1,
            minWidth: '80px',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: '14px',
            color: 'var(--text-dark)',
            fontFamily: 'inherit'
          }}
        />
      </div>
      <p style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
        💡 اكتب الوظيفة واضغط Enter لإضافة عدة وظائف (مثل: مهندس، طبيب، معلم)
      </p>
    </div>
  );
};
